import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Col, Row } from "reactstrap";

import { Attendee, EventsPeriod } from "../../api";

import {
  AttendeesFilters,
  fetchAttendeesForPeriod,
  fetchAttendeeStatisticsForPeriod,
  showAttendeeInfo,
  setAttendeeInfo,
  hideAttendeeInfo,
} from "../../store/actions/attendees.actions";
import { setEventsPeriod, EventsFilters } from "../../store/actions/events.actions";
import { useTypedSelector } from "../../store/models/root.interface";

import DateRange from "../../common/components/DateRange";
import DataTable from "../../common/components/ReactTable";
import Columns from "../../common/components/ReactTable/columns/AttendeesListColumns";
import TitleMeta from "../../common/components/TitleMeta";
import ErrorBoundary from "../../common/helpers/ErrorBoundary";

import Layout from "../../components/Layout/Layout";

import AttendeesSidebarRoot from "./Sidebar/AttendeesSidebarRoot";

const AttendeesList = () => {

  const dispatch = useDispatch();

  const [items, setItems] = useState<Attendee[]>([]);
  const [filters, setFilters] = useState<EventsFilters>();
  const [hasLoaded, setHasLoaded] = useState(false);

  const period = useTypedSelector(state => state.event.period);
  const selectedAttendeeId = useTypedSelector(state => state.attendees.attendeeId);
  const state = useTypedSelector(state => state.attendees.data);

  const isLoading = useMemo(() => state === "loading" && !hasLoaded, [state, hasLoaded]);
  const isFiltering = useMemo(() => state === "loading" && hasLoaded, [state, hasLoaded]);
  const isError = useMemo(() => state === "error", [state]);

  useEffect(() => {
    if (state && typeof state !== "string") {
      setItems(state.slice());
      setHasLoaded(true);
    }
  }, [state]);

  // Fetch attendee data
  useEffect(() => {
    async function fetchData() {
      dispatch(hideAttendeeInfo());
      dispatch(fetchAttendeesForPeriod(period, filters));
      dispatch(fetchAttendeeStatisticsForPeriod(period, filters));
    }
    fetchData();
  }, [period, filters]);

  // Handle date range onChange
  const handleSelectDateRange = (period: EventsPeriod) => {
    dispatch(setEventsPeriod(period));
  };

  const handleSelect = (attendee: Attendee) => {
    if (attendee.id !== selectedAttendeeId) {
      dispatch(showAttendeeInfo(attendee.id));
      dispatch(setAttendeeInfo(attendee.id, attendee));
    }
    else {
      dispatch(hideAttendeeInfo());
    }
  };

  const handleFilters = (data: { id: string; value: any }[]) => {
    let filters: AttendeesFilters = {};
    if (data.length) {
      filters = {};
      for (let i = 0; i < data.length; i++) {
        let key = data[i].id;
        let value = data[i].value;
        if (value) {
          switch (key) {
            case "name":
              filters.name = value;
              break;
            case "company":
              filters.company = value;
              break;
            case "email":
              filters.email = value;
              break;
          }
        }
      }
    }
    setFilters(Object.keys(filters).length ? filters : void 0);
  };

  return (
    <>
      <TitleMeta title="Attendees" />

      <Layout.SidebarPage>

        <Layout.Main>

          <Layout.Header>
            <Layout.Breadcrumb items={[["Events", "/events"]]} />
            <div className="d-flex align-items-center">
              <Layout.Title>Attendees</Layout.Title>
              <div>
                <DateRange
                  onChange={(e: any) => handleSelectDateRange(e)}
                  value={period}
                />
              </div>
            </div>
          </Layout.Header>

          <Layout.Body>
            {isError ? (
              <p className="py-2">Sorry, something went wrong.</p>
            ) : (
              <ErrorBoundary>
                <DataTable
                  manualFilters
                  columns={Columns}
                  data={items}
                  selected={attendee => attendee.id === selectedAttendeeId}
                  onSelect={handleSelect}
                  onChangeFilter={handleFilters}
                  isLoading={isLoading}
                  isFiltering={isFiltering}
                  messages={{
                    noRecords: "No attendees found",
                    noMatches: "No matching attendees found",
                  }}
                />
              </ErrorBoundary>
            )}
          </Layout.Body>

        </Layout.Main>

        <Layout.Sidebar>
          <AttendeesSidebarRoot />
        </Layout.Sidebar>

      </Layout.SidebarPage>
    </>
  );
};

export default AttendeesList;
